import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import Button from '../../Button/Button';
import SectionLoader from '../../Loader/SectionLoader';
import RowItemElement from '../../Buy/RowItem';
import SelectPaymentOptions from '../../Buy/SelectPaymentOptions';
import AddDeliveryAddressModal from '../Address/DeliveryAddressModal';
import SellFromHomeQuantityStepper from './SellFromHomeQuantityStepper';
import SellFromHomePriceGuaranteeCard from './SellFromHomePriceGuaranteeCard';
import SellFromHomePreAuthDrawer from './SellFromHomePreAuthDrawer';
import { ADD_ADDRESS_WIZARD } from '../../../constants/sessionStorage';
import { removeFromSessionStorage } from '../../../util/helpers/sessionStorageHelper';
import { fetchProductSymbolPrices } from '../../../store/actions/products/productActions';
import { fetchPortfolioBalances } from '../../../store/actions/portfolio/portfolioActions';
import { getProductBySymbol } from '../../../store/selectors/productsSelector';
import { selectBalancesAvailable } from '../../../store/selectors/portfolioBalancesSelectors';
import { getCurrentAccountUid } from '../../../store/selectors/accountSelectors';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import { PRODUCT_SYMBOL_PRICES_LOADING } from '../../../store/actions/products/productActionConstants';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import { ReactComponent as SfhCreditCard } from '../../../assets/images/svg/sfh-credit-card.svg';
import {
  PAYMENT_METHOD_TYPES,
  PAYMENT_METHOD_TYPES_ICONS,
} from '../../../util/enum/api/paymentTypes';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${pxToRem(12)};
  padding-top: ${pxToRem(8)};
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  min-width: 0;
`;

const ProductTitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(20)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
`;

const ProductWeight = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorTextSecondary};
`;

const UnitPrice = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(20)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  white-space: nowrap;
`;

const EmptyHint = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 600;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(6)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
`;

const UnitsTotal = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(6)};
  padding: ${pxToRem(12)} ${pxToRem(16)};

  > b {
    color: ${themeColors.colorTextPrimary};
    font-weight: 700;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
  padding: 0 ${pxToRem(64)};
`;

const OverLimitSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
  padding-bottom: ${pxToRem(12)};
`;

const WarningPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(8)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(6)};
  padding: ${pxToRem(12)} ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
    flex-shrink: 0;
  }
`;

const NeedHelpLink = styled.a`
  display: block;
  text-align: center;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const Spacer = styled.div`
  flex: 1;
  min-height: ${pxToRem(16)};
`;

const Footer = styled.div`
  margin-top: ${pxToRem(16)};

  > button {
    width: 100%;
  }
`;

const ORDER_LIMIT = 200000;

const findDefaultPaymentMethod = (paymentMethods) => {
  if (!Array.isArray(paymentMethods) || paymentMethods.length === 0)
    return null;
  return (
    paymentMethods.find((m) => m?.IsDefault) ||
    paymentMethods.find((m) => m?.CreditCardType) ||
    paymentMethods[0]
  );
};

const formatPaymentMethodLabel = (paymentMethod, t) => {
  if (!paymentMethod) return '';
  if (
    paymentMethod.PaymentMethodType === PAYMENT_METHOD_TYPES.ACCOUNT_BALANCE
  ) {
    return t('buyWizard.payment.paymentMethods.AccountBalance');
  }
  if (paymentMethod.PaymentMethodType === PAYMENT_METHOD_TYPES.ACH) {
    return `${paymentMethod.BankName}, ***${paymentMethod.BankAccountMask}`;
  }
  if (paymentMethod.PaymentMethodType === PAYMENT_METHOD_TYPES.CREDIT_CARD) {
    return `${t('buyWizard.payment.paymentMethods.CreditCard')} ***${
      paymentMethod.Last4Digits
    }`;
  }
  return '';
};

const SellFromHomeQuantity = ({ selectedHit, handleNext }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const productBySymbol = useSelector(getProductBySymbol);
  const balances = useSelector(selectBalancesAvailable);
  const isLoadingPrice = useSelector(
    selectIsLoadingByActionTypes([PRODUCT_SYMBOL_PRICES_LOADING]),
  );

  const [quantity, setQuantity] = useState(0);
  const [isPreAuthOpen, setIsPreAuthOpen] = useState(false);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  const productCode = selectedHit?.Code;

  useEffect(() => {
    if (accountUid && productCode) {
      dispatch(
        fetchProductSymbolPrices({
          accountUid,
          symbol: productCode,
          side: 'sell',
        }),
      );
    }
  }, [accountUid, productCode, dispatch]);

  useEffect(() => {
    if (accountUid && !balances?.PaymentMethods) {
      dispatch(fetchPortfolioBalances({ accountUid }));
    }
  }, [accountUid, balances?.PaymentMethods, dispatch]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setQuantity((q) => {
          const max = maxQuantityRef.current;
          return max != null ? Math.min(max, q + 1) : q + 1;
        });
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setQuantity((q) => Math.max(0, q - 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const hitPrice =
    selectedHit?.ProductPrice ??
    selectedHit?.SellPrice ??
    selectedHit?.BidPrice ??
    selectedHit?.PricePerUnit ??
    selectedHit?.SellPricePerUnit;
  const apiPrice = productBySymbol?.ProductPrice;
  const unitPrice = Number(hitPrice ?? apiPrice ?? 0) || 0;

  const maxQuantity =
    unitPrice > 0 ? Math.floor(ORDER_LIMIT / unitPrice) + 1 : null;
  const maxQuantityRef = useRef(maxQuantity);
  maxQuantityRef.current = maxQuantity;

  if (process.env.NODE_ENV !== 'production' && selectedHit && unitPrice === 0) {
    // eslint-disable-next-line no-console
    console.warn('[SFH] no price field on hit', {
      hitKeys: Object.keys(selectedHit),
      productBySymbolKeys: productBySymbol
        ? Object.keys(productBySymbol)
        : null,
    });
  }

  const defaultPaymentMethod = useMemo(
    () => findDefaultPaymentMethod(balances?.PaymentMethods),
    [balances],
  );

  useEffect(() => {
    if (!selectedPaymentMethod && defaultPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod);
    }
  }, [defaultPaymentMethod, selectedPaymentMethod]);

  const activePaymentMethod = selectedPaymentMethod || defaultPaymentMethod;

  const orderTotal = unitPrice * quantity;
  const isOverLimit = orderTotal > ORDER_LIMIT;
  const supportPhone = t('sellWizard.sfhWizard.phone');

  const handleContinue = () => {
    // TODO(SFH quote/lock endpoint): make a network call here once the SFH
    // quote/lock endpoint is available; gate handleNext() on success.
    handleNext();
  };

  return (
    <SectionLoader isLoading={isLoadingPrice}>
      <ModalBody>
        <Header>
          <HeaderLeft>
            <ProductTitle data-cy="text-sfh-product-title">
              {selectedHit?.Caption}
            </ProductTitle>
            <ProductWeight>{selectedHit?.WeightCaption}</ProductWeight>
          </HeaderLeft>
          <UnitPrice data-cy="text-sfh-unit-price">
            {formatMoneyNumeral(unitPrice)}
          </UnitPrice>
        </Header>

        <SellFromHomeQuantityStepper
          value={quantity}
          onChange={setQuantity}
          max={maxQuantity}
        />

        <Body>
          {quantity === 0 ? (
            <EmptyHint data-cy="text-sfh-select-quantity">
              {t('sellWizard.sfhWizard.quantity.selectQuantity')}
            </EmptyHint>
          ) : (
            <>
              <UnitsTotal data-cy="text-sfh-units-total">
                {t('sellWizard.sfhWizard.quantity.unitsLabel', {
                  count: quantity,
                })}
                {' = '}
                <b>{formatMoneyNumeral(orderTotal)}</b>
              </UnitsTotal>
              {!isOverLimit && (
                <SellFromHomePriceGuaranteeCard
                  price={unitPrice}
                  onLearnMore={() => setIsPreAuthOpen(true)}
                />
              )}
            </>
          )}
        </Body>

        <Spacer />

        {quantity > 0 && isOverLimit && (
          <OverLimitSection>
            <WarningPill data-cy="text-sfh-order-limit-warning">
              <AmberAlertTriangle />
              {t('sellWizard.sfhWizard.quantity.orderLimitWarning')}
            </WarningPill>
            <NeedHelpLink href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}>
              {t('sellWizard.sfhWizard.needHelp')} {supportPhone}
            </NeedHelpLink>
          </OverLimitSection>
        )}

        {quantity > 0 && activePaymentMethod && (
          <RowItemElement
            label={t('sellWizard.sfhWizard.quantity.payWith')}
            value={
              <>
                {activePaymentMethod.PaymentMethodType ===
                PAYMENT_METHOD_TYPES.CREDIT_CARD ? (
                  <SfhCreditCard />
                ) : (
                  PAYMENT_METHOD_TYPES_ICONS[
                    activePaymentMethod.PaymentMethodType
                  ]
                )}
                {formatPaymentMethodLabel(activePaymentMethod, t)}
              </>
            }
            clickLabel={t('sellWizard.sfhWizard.quantity.change')}
            onClick={() => setIsPaymentDrawerOpen(true)}
          />
        )}

        <Footer>
          <Button
            variant="primary"
            textTransform="none"
            disabled={quantity === 0 || isOverLimit}
            onClick={handleContinue}
            data-cy="button-sfh-continue-shipping"
          >
            {t('sellWizard.sfhWizard.quantity.continueToShipping')}
          </Button>
        </Footer>
      </ModalBody>
      <SellFromHomePreAuthDrawer
        isOpen={isPreAuthOpen}
        onClose={() => setIsPreAuthOpen(false)}
      />
      <SelectPaymentOptions
        open={isPaymentDrawerOpen}
        setOpen={setIsPaymentDrawerOpen}
        paymentMethods={balances?.PaymentMethods}
        selectedPaymentMethod={activePaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        accountUid={accountUid}
        isAddPaymentMethodOpen={isAddPaymentMethodOpen}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        isAddAddressOpen={isAddAddressOpen}
        setIsAddAddressOpen={setIsAddAddressOpen}
        wizardData={{}}
        setWizardData={() => {}}
        setSuccessToast={() => {}}
      />
      {isAddAddressOpen && (
        <AddDeliveryAddressModal
          isOpen={isAddAddressOpen}
          onClose={() => {
            removeFromSessionStorage(ADD_ADDRESS_WIZARD);
            setIsAddAddressOpen(false);
          }}
          title={t('buyWizard.addYourDeliveryAddress')}
          page="address"
          avoidClearingStorage
          isAddAddressOpen={isAddAddressOpen}
          setIsAddAddressOpen={setIsAddAddressOpen}
        />
      )}
    </SectionLoader>
  );
};

SellFromHomeQuantity.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    ProductPrice: PropTypes.number,
    SellPrice: PropTypes.number,
    BidPrice: PropTypes.number,
    PricePerUnit: PropTypes.number,
    SellPricePerUnit: PropTypes.number,
  }),
  handleNext: PropTypes.func,
};

export default SellFromHomeQuantity;
